import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import { Box, Hammer, Plus, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/common/page-header';
import { DataTable, type Column } from '@/components/common/data-table';
import { EmptyState } from '@/components/common/states';
import { useConfirm } from '@/components/common/confirm-dialog';
import { SearchInput } from '@/components/common/search-input';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardHeaderText,
  CardTitle,
} from '@/components/ui/card';
import { Badge, StatusBadge } from '@/components/ui/badge';
import { Field } from '@/components/ui/field';
import { Input, CodeTextarea } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { useToast } from '@/components/ui/toast';
import { useBuildLogs, useDockerfile, useEnvironments, useProjects, qk, useInvalidate, useCan } from '@/lib/api/queries';
import { environmentsApi } from '@/lib/api/endpoints';
import { useI18n, useT } from '@/lib/i18n';
import { formatRelative } from '@/lib/format';
import { presentIdes } from '@/lib/presenters';
import { slugify } from '@/lib/format';
import type { Environment, EnvironmentRevision, Project } from '@/lib/api/types';

// English, like the Dockerfile it seeds: this is file content the user edits
// and ships, not interface copy, so it does not belong in the translation
// catalogue and must not be French there either.
// The base is the image this installation runs for Jupyter, by its full
// reference when the catalogue has answered; "noryx-jupyter" otherwise, which
// the platform resolves to the same thing. A bare "noryx-python:latest" used
// to be seeded here, and kaniko looked for it on Docker Hub.
const blankDockerfile = (base: string) => `# Noryx environment
# Add your libraries below, then build a revision.
FROM ${base}

RUN pip install --no-cache-dir \\
    pandas \\
    scikit-learn
`;

/**
 * Where a build runs. Required, and never silently defaulted to the first
 * project: a build consumes that project's quota and its result is attributed
 * to it, so guessing would spend someone's budget on their behalf.
 */
function BuildProjectField({
  projects,
  value,
  onChange,
}: {
  projects: Project[];
  value: string;
  onChange: (value: string) => void;
}) {
  const t = useT();
  return (
    <Field
      label={t('environments.buildProject')}
      description={t('environments.buildProjectHint')}
      required
    >
      <Select
        value={value}
        onValueChange={onChange}
        placeholder={t('environments.buildProjectPlaceholder')}
        options={projects.map((project) => ({ value: project.id, label: project.name }))}
      />
    </Field>
  );
}

function CreateEnvironmentSheet({
  projects,
  projectId,
  onProjectChange,
  environments,
  open,
  onOpenChange,
}: {
  projects: Project[];
  projectId: string;
  onProjectChange: (value: string) => void;
  environments: Environment[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const t = useT();
  const toast = useToast();
  const invalidate = useInvalidate();

  const [name, setName] = React.useState('');
  const [baseId, setBaseId] = React.useState('');
  const systemJupyter = environments.find((environment) => environment.latestBuildId === 'system-jupyter' || environment.id === 'system-jupyter');
  const blank = blankDockerfile(systemJupyter?.destinationImage || 'noryx-jupyter');
  const [dockerfile, setDockerfile] = React.useState(blank);
  const [touched, setTouched] = React.useState(false);

  const base = environments.find((environment) => environment.id === baseId);

  // Building *on* the base rather than copying it. Pasting the base's whole
  // Dockerfile lost the parentage - nothing afterwards said what this
  // environment derived from - and rebuilt every one of its layers from
  // scratch, which on the platform's own Python image is several minutes of
  // pip for a line somebody wanted to add at the end. A FROM against the
  // image already in the registry says where it comes from and builds in
  // seconds.
  React.useEffect(() => {
    if (baseId && base?.destinationImage) {
      setDockerfile(`FROM ${base.destinationImage}\n\n# ${t('environments.forkHint')}\n`);
      return;
    }
    if (!baseId) setDockerfile(blank);
  }, [baseId, base?.destinationImage, base?.name, blank]);

  React.useEffect(() => {
    if (!open) {
      setName('');
      setBaseId('');
      setTouched(false);
    }
  }, [open]);

  const slug = slugify(name);
  const nameError = touched && !slug ? t('common.required') : undefined;
  const projectError = touched && !projectId ? t('common.required') : undefined;

  const mutation = useMutation({
    mutationFn: () =>
      environmentsApi.createBuild({
        projectId,
        name: slug,
        dockerfileContent: dockerfile,
        contextPath: '.',
      }),
    onSuccess: () => {
      invalidate(qk.environments(), qk.builds(projectId));
      onOpenChange(false);
      toast.success(t('environments.buildStarted'), t('environments.title'));
    },
    onError: (error) => toast.error(error, t('environments.createTitle')),
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent size="lg" aria-describedby={undefined}>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setTouched(true);
            if (slug && projectId) mutation.mutate();
          }}
          className="flex min-h-0 flex-1 flex-col"
        >
          <SheetHeader>
            <SheetTitle>{t('environments.createTitle')}</SheetTitle>
            <SheetDescription>{t('environments.createHint')}</SheetDescription>
          </SheetHeader>
          <SheetBody>
            <BuildProjectField projects={projects} value={projectId} onChange={onProjectChange} />
            {projectError ? (
              <p className="text-xs text-danger">{projectError}</p>
            ) : null}
            <Field
              label={t('environments.nameLabel')}
              description={t('environments.nameHint')}
              error={nameError}
              required
            >
              <Input
                value={name}
                onChange={(event) => setName(event.target.value)}
                onBlur={() => setTouched(true)}
                placeholder={t('environments.namePlaceholder')}
                maxLength={63}
                autoFocus
              />
            </Field>
            <Field label={t('environments.baseLabel')} description={t('environments.baseHint')}>
              <Select
                value={baseId}
                onValueChange={setBaseId}
                placeholder={t('environments.baseEmpty')}
                options={[
                  { value: '', label: t('environments.baseEmpty') },
                  ...environments
                    .filter((environment) => environment.latestBuildId)
                    .map((environment) => ({ value: environment.id, label: environment.name })),
                ]}
              />
            </Field>
            <Field label={t('environments.definition')} description={t('environments.definitionHint')}>
              <CodeTextarea
                value={dockerfile}
                onChange={(event) => setDockerfile(event.target.value)}
              />
            </Field>
          </SheetBody>
          <SheetFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary" loading={mutation.isPending} disabled={!slug || !projectId}>
              <Hammer aria-hidden />
              {t('environments.build')}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function EnvironmentDetail({
  environment,
  projects,
  projectId,
  onProjectChange,
  onClose,
}: {
  environment: Environment;
  projects: Project[];
  projectId: string;
  onProjectChange: (value: string) => void;
  onClose: () => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const toast = useToast();
  const invalidate = useInvalidate();
  const dockerfile = useDockerfile(environment.latestBuildId);
  const [draft, setDraft] = React.useState<string | null>(null);
  // The build this panel started, so its output can be shown here rather than
  // left in a pod nobody can reach. A build that fails is the normal case when
  // somebody is editing a Dockerfile, and "failed" on its own is not a message
  // anybody can act on.
  const [startedBuildId, setStartedBuildId] = React.useState<string | null>(null);
  const buildLogs = useBuildLogs(startedBuildId ?? undefined);

  const { dialog: revisionDialog, ask } = useConfirm();

  // Removing one revision, which the API has always allowed and nothing
  // offered.
  const removeRevision = useMutation({
    mutationFn: (buildId: string) => environmentsApi.cancelBuild(buildId),
    onSuccess: () => {
      invalidate(qk.environments(), qk.builds(projectId));
      toast.success(t('environments.revisionDeleted'), environment.name);
    },
    onError: (error) => toast.error(error, t('environments.deleteRevision')),
  });

  // A system environment is what the installation is configured to run, and a
  // rebuild of one pushes into the platform's own repository - which the
  // platform then refuses to start, because it starts the reference it was
  // configured with. Offered, it produced a build that spent eleven days
  // deciding which image the launch form sent. It is not offered any more, and
  // the reason is on screen rather than in a refusal at the end.
  const isSystemEnvironment = environment.category === 'system';

  const rebuild = useMutation({
    mutationFn: () =>
      environmentsApi.createBuild({
        projectId,
        name: environment.name,
        dockerfileContent: draft ?? dockerfile.data ?? '',
        contextPath: environment.revisions?.[0]?.contextPath ?? '.',
        // The repository this environment already lives in, without a tag: a
        // rebuild is a new revision of one environment, not a new one. The
        // platform adds the tag - a browser inventing one would either collide
        // with a revision or invent a naming scheme of its own.
        destinationImage: environment.destinationImage.replace(/:[^:/]+$/, ''),
      }),
    onSuccess: (created) => {
      invalidate(qk.environments(), qk.builds(projectId));
      setDraft(null);
      setStartedBuildId(created?.id ?? null);
      toast.success(t('environments.buildStarted'), environment.name);
    },
    onError: (error) => toast.error(error, t('environments.build')),
  });

  // Active means the one that starts, which is not always the newest.
  //
  // The badge followed the most recent build. For a system environment that a
  // project had rebuilt, the most recent build was the rebuild - and the
  // platform starts the reference it is configured with, so the screen marked
  // as active a revision nothing would ever run. Matching on the image answers
  // the question the badge is actually asked: which of these do I get.
  const activeRevisionId =
    (environment.revisions ?? []).find(
      (revision) => revision.destinationImage === environment.destinationImage,
    )?.buildId ?? environment.latestBuildId;

  const revisionColumns: Column<EnvironmentRevision>[] = [
    {
      id: 'build',
      header: t('production.revision'),
      cell: (revision) => (
        <span className="flex items-center gap-2">
          {/* The number, then the build it came from. "a449755a" is what the
              platform calls it; "révision 3" is what a person calls it, and
              only one of the two is worth reading first. */}
          <span className="font-medium">
            {t('environments.revisionNumber', { number: String(revision.number) })}
          </span>
          <span className="font-mono text-[0.6875rem] text-muted-foreground">
            {revision.buildId.slice(0, 8)}
          </span>
          {revision.buildId === activeRevisionId ? (
            <Badge tone="success">{t('environments.activeRevision')}</Badge>
          ) : null}
        </span>
      ),
    },
    {
      id: 'status',
      header: t('common.status'),
      cell: (revision) => (
        <div className="min-w-0 space-y-1">
          <StatusBadge status={revision.status} locale={locale} />
          {/* The reason beside the verdict, kept by the platform: the pod that
              said it is gone within the hour, and "failed" alone is not a
              message anybody can act on. */}
          {revision.status === 'failed' && revision.failureReason ? (
            <p className="max-w-md break-words font-mono text-xs text-muted-foreground" title={revision.failureReason}>
              {revision.failureReason}
            </p>
          ) : null}
        </div>
      ),
    },
    {
      // What this revision was made from and what it produced. A ref and a tag
      // both move, so a revision that records only those cannot be replayed
      // and cannot answer which source produced a running image.
      id: 'provenance',
      header: t('environments.provenance'),
      cell: (revision) => (
        <span className="flex flex-col gap-0.5 font-mono text-[0.6875rem] text-muted-foreground">
          {revision.commitSha ? (
            <span title={`${revision.gitRef || ''} ${revision.commitSha}`.trim()}>
              {revision.gitRef ? `${revision.gitRef} · ` : ''}
              {revision.commitSha.slice(0, 12)}
            </span>
          ) : null}
          {revision.imageDigest ? (
            <span title={revision.imageDigest}>
              {revision.imageDigest.replace(/^sha256:/, '').slice(0, 12)}
            </span>
          ) : null}
          {!revision.commitSha && !revision.imageDigest ? (
            <span className="font-sans">{t('environments.provenanceUnknown')}</span>
          ) : null}
        </span>
      ),
    },
    {
      id: 'createdAt',
      header: t('common.createdAt'),
      sortValue: (revision) => revision.createdAt,
      cell: (revision) => (
        <span className="text-xs text-muted-foreground">
          {formatRelative(revision.createdAt, locale)}
        </span>
      ),
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardHeaderText>
          <CardTitle>{environment.name}</CardTitle>
          <CardDescription>
            {presentIdes(environment.workspaceIdes)}
            {environment.latestImageSizeGiB ? ` · ${environment.latestImageSizeGiB} Gio` : ''}
          </CardDescription>
        </CardHeaderText>
        <div className="flex items-center gap-2">
          <StatusBadge status={environment.latestStatus} locale={locale} />
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        <Tabs defaultValue="definition">
          <TabsList>
            <TabsTrigger value="definition">{t('environments.definition')}</TabsTrigger>
            <TabsTrigger value="revisions">{t('environments.revisions')}</TabsTrigger>
          </TabsList>

          <TabsContent value="definition" className="space-y-3">
            <Field label={t('environments.definition')} description={t('environments.definitionHint')}>
              <CodeTextarea
                value={draft ?? dockerfile.data ?? ''}
                onChange={(event) => setDraft(event.target.value)}
                readOnly={dockerfile.isLoading}
              />
            </Field>
            <BuildProjectField projects={projects} value={projectId} onChange={onProjectChange} />
            <div className="flex justify-end gap-2">
              {draft !== null ? (
                <Button variant="secondary" onClick={() => setDraft(null)}>
                  {t('common.cancel')}
                </Button>
              ) : null}
              <Button
                variant="primary"
                loading={rebuild.isPending}
                onClick={() => rebuild.mutate()}
                disabled={dockerfile.isLoading || !projectId || isSystemEnvironment}
                title={isSystemEnvironment ? t('environments.systemRebuildHint') : undefined}
              >
                <Hammer aria-hidden />
                {t('environments.build')}
              </Button>
            </div>
            {isSystemEnvironment ? (
              <p className="text-xs text-muted-foreground">{t('environments.systemRebuildHint')}</p>
            ) : !projectId ? (
              // A primary button greyed out for a reason nobody states is a
              // screen that looks like it offers nothing.
              <p className="text-xs text-muted-foreground">{t('environments.buildNeedsProject')}</p>
            ) : null}

            {/* What the build is doing, here, while it does it. A rebuild used
                to end at a toast: a failure - a package that does not exist, a
                base image the cluster cannot pull - left nothing on the screen
                and the reason died with the pod. */}
            {startedBuildId ? (
              <div className="mt-4 rounded-md border border-border">
                <div className="flex items-center gap-2 border-b border-border px-3 py-2 text-xs">
                  <span className="font-medium">{t('environments.buildOutput')}</span>
                  {buildLogs.data?.status ? (
                    <Badge tone={buildLogs.data.status === 'failed' ? 'danger' : 'neutral'}>
                      {buildLogs.data.status}
                    </Badge>
                  ) : null}
                  {buildLogs.data?.pending ? (
                    <span className="text-muted-foreground">{t('environments.buildRunning')}</span>
                  ) : null}
                </div>
                <pre className="max-h-72 overflow-auto px-3 py-2 font-mono text-[11px] leading-relaxed">
                  {buildLogs.data?.logs || buildLogs.data?.unavailable || t('common.loading')}
                </pre>
              </div>
            ) : null}
          </TabsContent>

          <TabsContent value="revisions">
            <DataTable
              data={environment.revisions ?? []}
              columns={revisionColumns}
              rowKey={(revision) => revision.buildId}
              // A revision can be removed here, and only here.
              //
              // The API has always accepted it; nothing offered it, so a build
              // made by mistake stayed in an environment's history forever. One
              // of them - a rebuild pushed into the platform's own repository -
              // spent eleven days deciding which image the launch form sent.
              //
              // Not offered on the platform's own revisions: they are not
              // builds, they describe the image this installation is configured
              // with, and there is nothing to delete but the configuration.
              rowActions={(revision) =>
                revision.buildId.startsWith('system-') ? null : (
                  <DropdownMenuItem
                    destructive
                    onSelect={() =>
                      ask({
                        title: t('environments.deleteRevision'),
                        description: t('environments.deleteRevisionHint', {
                          image: revision.destinationImage || revision.buildId,
                        }),
                        confirmLabel: t('common.delete'),
                        destructive: true,
                        onConfirm: () => removeRevision.mutateAsync(revision.buildId),
                      })
                    }
                  >
                    <Trash2 aria-hidden />
                    {t('common.delete')}
                  </DropdownMenuItem>
                )
              }
              defaultSort={{ columnId: 'createdAt', direction: 'desc' }}
              emptyState={
                <EmptyState
                  compact
                  title={t('environments.revisions')}
                  description={t('environments.revisionsHint')}
                />
              }
            />
          </TabsContent>
        </Tabs>
      </CardContent>

      <CardFooter className="justify-between text-xs text-muted-foreground">
        <span className="truncate font-mono" title={environment.destinationImage}>
          {environment.destinationImage || '—'}
        </span>
        <span>{formatRelative(environment.updatedAt, locale)}</span>
      </CardFooter>
      {revisionDialog}
    </Card>
  );
}

/**
 * Environment catalogue.
 *
 * Environments moved out of the project because they never belonged to one:
 * `/api/v1/environments` is a platform endpoint with an optional project
 * filter, and an image built in one project is used by workspaces, jobs and
 * apps in others. Listing them per project made a shared asset look owned, and
 * hid every environment a user could actually launch.
 *
 * Building still happens somewhere, so the project is now chosen in the form
 * rather than inherited from the URL — the same decision, made visible.
 */
export function EnvironmentCatalog() {
  const t = useT();
  const can = useCan();
  const { locale } = useI18n();
  const toast = useToast();
  const invalidate = useInvalidate();
  const { dialog, ask } = useConfirm();

  const [search, setSearch] = React.useState('');
  const [creating, setCreating] = React.useState(false);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  // Which project a build runs in. Remembered across sheets so a user who
  // builds several environments in a row does not re-answer every time.
  const [buildProjectId, setBuildProjectId] = React.useState('');
  // Where the panel is, so opening one brings it into view. It renders below
  // the table, so clicking a row in a list of a dozen environments put the
  // only place a revision can be built off the bottom of the screen - which
  // reads exactly like the interface offering nothing.
  const panelRef = React.useRef<HTMLDivElement>(null);

  const projects = useProjects();
  const environments = useEnvironments();
  const selected = environments.data?.find((environment) => environment.id === selectedId) ?? null;

  // Rebuilding an existing environment builds in the project that built it.
  // Creating one still asks, and is never defaulted - a build spends that
  // project's quota - but for a revision the project is not a guess: it is
  // where the previous one was built.
  React.useEffect(() => {
    if (selected?.projectId) setBuildProjectId(selected.projectId);
  }, [selected?.id, selected?.projectId]);

  React.useEffect(() => {
    if (selectedId && panelRef.current) {
      panelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedId]);

  const remove = useMutation({
    mutationFn: (environmentId: string) => environmentsApi.remove(environmentId),
    onSuccess: () => {
      invalidate(qk.environments());
      setSelectedId(null);
    },
    onError: (error) => toast.error(error, t('environments.deleteTitle')),
  });

/**
 * What the registry found in the image.
 *
 * Rebuilding weekly picks up the base image's security fixes, and that is a
 * ritual until somebody can see whether it worked: "we rebuild" is an
 * intention, "3 critical" is a fact. Nothing here blocks anything - an image
 * with findings still runs.
 */
function Vulnerabilities({ environment }: { environment: Environment }) {
  const t = useT();
  const report = environment.vulnerabilities;
  // No report is not a clean report: a registry that does not scan says
  // nothing, and the screen says nothing back.
  if (!report) return <span className="text-xs text-muted-foreground">—</span>;
  // And "we could not ask" is not "we asked and found nothing". Both used to
  // render as an em dash, which reads as reassurance.
  if (report.status !== 'scanned') {
    return (
      <span
        className="text-xs text-muted-foreground underline decoration-dotted underline-offset-2"
        title={report.detail ?? undefined}
      >
        {report.status === 'unscanned'
          ? t('environments.notScanned')
          : t('environments.scanUnavailable')}
      </span>
    );
  }
  if (report.total === 0) {
    return <Badge tone="success">{t('environments.noVulnerabilities')}</Badge>;
  }
  return (
    <span className="flex flex-wrap items-center gap-1">
      {report.critical > 0 ? <Badge tone="danger">{report.critical} critical</Badge> : null}
      {report.high > 0 ? <Badge tone="warning">{report.high} high</Badge> : null}
      {report.critical === 0 && report.high === 0 ? (
        <Badge tone="outline">{report.total}</Badge>
      ) : null}
    </span>
  );
}

  const columns: Column<Environment>[] = [
    {
      id: 'name',
      header: t('common.name'),
      sortValue: (environment) => environment.name,
      searchValue: (environment) => environment.name,
      cell: (environment) => (
        <div className="min-w-0">
          <p className="truncate font-medium">{environment.name}</p>
          <p className="truncate text-xs text-muted-foreground">
            {presentIdes(environment.workspaceIdes)}
          </p>
        </div>
      ),
    },
    {
      id: 'category',
      header: t('common.type'),
      sortValue: (environment) => environment.category,
      cell: (environment) => (
        <Badge tone={environment.category === 'system' ? 'neutral' : 'brand'}>
          {environment.category === 'system' ? 'Système' : 'Personnalisé'}
        </Badge>
      ),
    },
    {
      id: 'status',
      header: t('common.status'),
      sortValue: (environment) => environment.latestStatus,
      cell: (environment) => <StatusBadge status={environment.latestStatus} locale={locale} />,
    },
    {
      id: 'vulnerabilities',
      header: t('environments.vulnerabilities'),
      sortValue: (environment) =>
        environment.vulnerabilities?.status === 'scanned'
          ? environment.vulnerabilities.critical * 1000 + environment.vulnerabilities.high
          : -1,
      cell: (environment) => <Vulnerabilities environment={environment} />,
    },
    {
      id: 'revisions',
      header: t('environments.revisions'),
      align: 'right',
      sortValue: (environment) => environment.revisions?.length ?? 0,
      cell: (environment) => (
        <span className="tabular-nums text-muted-foreground">{environment.revisions?.length ?? 0}</span>
      ),
    },
    {
      id: 'updatedAt',
      header: t('common.updatedAt'),
      sortValue: (environment) => environment.updatedAt,
      cell: (environment) => (
        <span className="text-xs text-muted-foreground">
          {formatRelative(environment.updatedAt, locale)}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('environments.title')}
        description={t('environments.subtitle')}
        actions={can('build-environment') ? (<Button variant="primary" onClick={() => setCreating(true)}>
            <Plus aria-hidden />
            {t('environments.create')}
          </Button>) : null}
      />

      {environments.data && environments.data.length > 0 ? (
        <SearchInput
          value={search}
          onValueChange={setSearch}
          label={t('common.search')}
          className="max-w-xs"
        />
      ) : null}

      <Card>
        <DataTable
          data={environments.data}
          columns={columns}
          rowKey={(environment) => environment.id}
          isLoading={environments.isLoading}
          isError={environments.isError}
          error={environments.error}
          onRetry={() => void environments.refetch()}
          search={search}
          onResetSearch={() => setSearch('')}
          onRowClick={(environment) => setSelectedId(environment.id)}
          selectedKey={selectedId}
          defaultSort={{ columnId: 'updatedAt', direction: 'desc' }}
          emptyState={
            <EmptyState
              icon={Box}
              title={t('environments.empty')}
              description={t('environments.emptyHint')}
              action={can('build-environment') ? (<Button variant="primary" onClick={() => setCreating(true)}>
                  <Plus aria-hidden />
                  {t('environments.create')}
                </Button>) : null}
            />
          }
          rowActions={(environment) => (
            <>
              {environment.category === 'system' ? null : (
                <DropdownMenuItem onSelect={() => setSelectedId(environment.id)}>
                  <Hammer aria-hidden />
                  {t('environments.build')}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onSelect={() => setSelectedId(environment.id)}>
                {t('common.open')}
              </DropdownMenuItem>
              <DropdownMenuItem
                destructive
                disabled={environment.category === 'system'}
                onSelect={() =>
                  ask({
                    title: t('environments.deleteTitle'),
                    description: t('environments.deleteWarning'),
                    confirmLabel: t('common.delete'),
                    destructive: true,
                    confirmationValue: environment.name,
                    confirmationLabel: t('environments.nameLabel'),
                    onConfirm: () => remove.mutateAsync(environment.id),
                  })
                }
              >
                <Trash2 aria-hidden />
                {t('common.delete')}
              </DropdownMenuItem>
            </>
          )}
        />
      </Card>

      <div ref={panelRef} />
      {selected ? (
        <EnvironmentDetail
          environment={selected}
          projects={projects.data ?? []}
          projectId={buildProjectId}
          onProjectChange={setBuildProjectId}
          onClose={() => setSelectedId(null)}
        />
      ) : null}

      <CreateEnvironmentSheet
        projects={projects.data ?? []}
        projectId={buildProjectId}
        onProjectChange={setBuildProjectId}
        environments={environments.data ?? []}
        open={creating}
        onOpenChange={setCreating}
      />
      {dialog}
    </div>
  );
}
